import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { Avatar } from '../ui/Avatar';
import { fmtEur, euro } from '../../lib/utils';

interface Props {
  onClose: () => void;
  projectId: string;
}

const ROLES = ['PM', 'Backend Lead', 'Frontend', 'Designer', 'QA', 'Account', 'Strategy', 'Content', 'DevOps', 'Specialist'];

export function AssignResourceModal({ onClose, projectId }: Props) {
  const qc = useQueryClient();
  const [type, setType] = useState<'internal' | 'external'>('internal');
  const [userId, setUserId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [role, setRole] = useState('PM');
  const [hourlyRate, setHourlyRate] = useState(70);
  const [hoursPlanned, setHoursPlanned] = useState(40);

  const { data: users } = useQuery({
    queryKey: ['users-lite'],
    queryFn: () => api.get<{ items: Array<{ id: string; firstName: string; lastName: string; initials: string; avatarColor: string; role: { name: string } }> }>('/users?pageSize=200'),
    enabled: type === 'internal',
  });
  const { data: suppliers } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => api.get<{ items: Array<{ id: string; fullName: string; city: string | null; hourlyRate: string | null }> }>('/suppliers?pageSize=200'),
    enabled: type === 'external',
  });

  const assign = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/members`, {
      userId: type === 'internal' ? userId : null,
      supplierId: type === 'external' ? supplierId : null,
      role,
      hourlyRate,
      hoursPlanned,
      isInternal: type === 'internal',
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      qc.invalidateQueries({ queryKey: ['project-team-progress', projectId] });
      toast.ok('Risorsa assegnata', `${role} · ${hoursPlanned}h`);
      onClose();
    },
    onError: (err) => toast.alert('Errore assegnazione', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const totalCost = hourlyRate * hoursPlanned;
  const canSubmit = type === 'internal' ? !!userId : !!supplierId;

  return (
    <Modal
      title="Assegna risorsa"
      kicker="Team · Allocazione"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <span className="mute mono text-[12px]">Costo stimato: {euro(totalCost)}</span>
          <button className="btn btn-primary" onClick={() => assign.mutate()} disabled={!canSubmit || assign.isPending}>
            Assegna <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-section">
        <div className="form-section-hd">Tipo collaborazione</div>
        <div className="seg-control">
          <button aria-pressed={type === 'internal'} onClick={() => { setType('internal'); setSupplierId(''); }}>Interno</button>
          <button aria-pressed={type === 'external'} onClick={() => { setType('external'); setUserId(''); }}>Esterno (Professionista)</button>
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-hd">Persona</div>
        {type === 'internal' ? (
          <div className="grid grid-cols-2 gap-2 max-h-[260px] overflow-y-auto">
            {(users?.items ?? []).map((u) => (
              <div
                key={u.id}
                onClick={() => setUserId(u.id)}
                className="flex items-center gap-2.5 p-2.5 border rounded-2 cursor-pointer hover:bg-paper-2 transition"
                style={{
                  borderColor: userId === u.id ? 'var(--accent)' : 'var(--line)',
                  background: userId === u.id ? 'var(--paper-2)' : 'var(--surface)',
                }}
              >
                <Avatar initials={u.initials} size={28} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[13px] truncate">{u.firstName} {u.lastName}</div>
                  <div className="mute text-[12px] truncate">{u.role.name}</div>
                </div>
                {userId === u.id && <Icon name="check" size={14} />}
              </div>
            ))}
            {!users?.items.length && <div className="col-span-2 text-center py-8 mute text-[13px]">Nessun utente</div>}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 max-h-[260px] overflow-y-auto">
            {(suppliers?.items ?? []).map((s) => (
              <div
                key={s.id}
                onClick={() => { setSupplierId(s.id); if (s.hourlyRate) setHourlyRate(Number(s.hourlyRate)); }}
                className="flex items-center gap-2.5 p-2.5 border rounded-2 cursor-pointer hover:bg-paper-2"
                style={{
                  borderColor: supplierId === s.id ? 'var(--accent)' : 'var(--line)',
                  background: supplierId === s.id ? 'var(--paper-2)' : 'var(--surface)',
                }}
              >
                <Avatar initials={s.fullName.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase()} size={28} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[13px] truncate">{s.fullName}</div>
                  <div className="mute text-[12px] truncate">{s.city ?? '—'}{s.hourlyRate && ` · € ${Number(s.hourlyRate)}/h`}</div>
                </div>
                {supplierId === s.id && <Icon name="check" size={14} />}
              </div>
            ))}
            {!suppliers?.items.length && <div className="col-span-2 text-center py-8 mute text-[13px]">Nessun professionista</div>}
          </div>
        )}
      </div>

      <div className="form-section">
        <div className="form-section-hd">Allocazione</div>
        <div className="form-grid">
          <Field label="Ruolo nel progetto" span={6}>
            <select className="inp" value={role} onChange={(e) => setRole(e.target.value)}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Tariffa oraria (€/h)" span={3}>
            <input className="inp mono text-right" type="number" value={hourlyRate} onChange={(e) => setHourlyRate(Number(e.target.value))} />
          </Field>
          <Field label="Ore pianificate" span={3}>
            <input className="inp mono text-right" type="number" value={hoursPlanned} onChange={(e) => setHoursPlanned(Number(e.target.value))} />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
